import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * PublicLanguageSelector - Language selector for public pages
 * 
 * Features:
 * - Uses URL parameters to persist language preference: ?language=en|fr|nl|es|de
 * - Updates URL when user changes language through selector
 * 
 * Usage examples:
 * - https://example.com/jobs/123/public?language=fr (sets French)
 * - https://example.com/jobs/org/456/public?language=nl (sets Dutch)
 */

const SUPPORTED_LANGUAGES = ['en', 'fr', 'nl', 'es', 'de'];

const getUrlParameter = (name: string): string | null => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    } catch (error) {
        console.error('[PublicLanguageSelector] Error parsing URL parameters:', error);
        return null;
    }
};

const updateUrlParameter = (name: string, value: string) => {
    try {
        const url = new URL(window.location.href);
        url.searchParams.set(name, value);
        window.history.replaceState({}, '', url.toString());
    } catch (error) {
        console.error('[PublicLanguageSelector] Error updating URL parameter:', error);
    }
};

interface PublicLanguageSelectorProps {
    iconOnly?: boolean;
    className?: string;
}

const PublicLanguageSelector: React.FC<PublicLanguageSelectorProps> = ({
    iconOnly = false,
    className = ""
}) => {
    const { i18n } = useTranslation(); const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

    // Listen to i18n language changes to update our state
    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            setCurrentLanguage(lng);
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n]);	// Initialize language from URL parameter on mount
    useEffect(() => {
        const urlLanguage = getUrlParameter('language');

        if (urlLanguage && SUPPORTED_LANGUAGES.includes(urlLanguage)) {
            if (urlLanguage !== i18n.language) {
                i18n.changeLanguage(urlLanguage);
                setCurrentLanguage(urlLanguage);
            } else {
                setCurrentLanguage(urlLanguage);
            }
        }
    }, []); // Empty dependency array to run only on mount
    const changeLanguage = (lng: string) => {
        // Validate that the language is supported
        if (!SUPPORTED_LANGUAGES.includes(lng)) {
            console.warn(`Unsupported language: ${lng}. Supported languages are: ${SUPPORTED_LANGUAGES.join(', ')}`);
            return;
        }

        // Change the language in i18n
        i18n.changeLanguage(lng);
        setCurrentLanguage(lng);

        // Update URL parameter
        updateUrlParameter('language', lng);
    }; return (
        <div className={`flex items-center space-x-2 ${className}`}>
            <Select
                value={currentLanguage}
                onValueChange={changeLanguage}
            >
                <SelectTrigger
                    hideChevron={iconOnly}
                    className={iconOnly
                        ? "h-9 w-9 p-0 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-600 focus:border-blue-500 transition-colors duration-150 rounded-sm"
                        : "h-9 px-4 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-600 focus:border-blue-500 transition-colors duration-150 rounded-sm flex items-center"
                    }
                >
                    <Globe className={`${iconOnly ? 'h-5 w-5 mx-auto' : 'h-4 w-4 mr-2'} text-current`} />
                    {!iconOnly && <SelectValue placeholder="Select language" />}
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 z-50 border border-gray-200 dark:border-gray-700 shadow-lg rounded-sm">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                </SelectContent>
            </Select>
        </div>
    );
};

export default PublicLanguageSelector;
